import { useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { getFeedsReplyComments } from '../../services';
import React from 'react';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { Table } from 'tea-component';
import { sentimentTypeMap } from './constants';
import ShareBtn from '../../pages/feeds/Details/components/ShareBtn';
import { getFeedsContentText } from '../../utils/languageValue';
import DboExpandCollapseText from '../ExpandCollapse';
import { reviewsFormat } from '@op/utils/value';

const { pageable } = Table.addons;

function ShowContext({ data, language, showSentiment }: any) {
  let { sentiment_rating, reviewer } = data || {};
  const content = getFeedsContentText(data, language);
  return (
    <>
      <div className="table-svg-flex">
        {showSentiment && (
          <SvgIcon
            type={sentimentTypeMap[sentiment_rating]}
            className="table-svg-flex-icon"
          />
        )}
        <span className="table-svg-flex-text">{reviewer}</span>
      </div>
      <div className="table-svg-content">
        <DboExpandCollapseText
          text={content}
          rowNum={3}
          className="table-svg-title"
        ></DboExpandCollapseText>
      </div>
    </>
  );
}

export default function useReviewReplyData({
  userInfo,
  gameInfo,
  itemData,
  language,
  text = 'Details',
  showSentiment = true,
  feedsSource,
  setTableLoading
}: any) {
  const [sort, setSort] = useState<any>({
    by: 'comment_time',
    order: 'desc',
  });
  const { formatMessage } = useIntl();
  const [tableList, setTableList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [showDetailColumn, setShowDetailColumn] = useState(false);
  const [sentimentInfo, setSentimentInfo] = useState({
    bad_review: 0,
    good_review: 0,
    neutral_review: 0,
  });
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    page_size: 10,
  });

  const params = useMemo(() => {
    let { comment_uin, channel_type, comment_time } = itemData || {};
    const params = {
      user_id: userInfo.userid,
      is_valid: 0,
      sort_model: sort.by,
      comment_uin: comment_uin,
      channel_type: channel_type,
      sort: sort.order,
      ...pageInfo,
      ...gameInfo,
    };
    if (comment_time) {
      params.comment_time = comment_time;
    }
    if (feedsSource) {
      params.feed_source = feedsSource;
    }

    return params;
  }, [pageInfo, sort, userInfo, itemData]);

  // 获取弹窗的数据 0是降序
  async function getCommentData() {
    setLoading(true);
    setTableLoading?.(true);
    let result: any = await getFeedsReplyComments(params);
    let { list, total, ...otherInfo } = result || {};
    list = list || [];
    const showDetailColumn = list.some((item: any) => item?.content_url);
    setTotal(total || 0);
    setTableList(list || []);
    setShowDetailColumn(showDetailColumn);
    setSentimentInfo(
      otherInfo || { bad_review: 0, good_review: 0, neutral_review: 0 },
    );
    setLoading(false);
    setTableLoading?.(false);
  }

  useEffect(() => {
    if (userInfo && itemData) {
      getCommentData();
    }
  }, [params]);

  const tableCommon = useMemo(() => {
    let list: any = [
      {
        key: 'id',
        header: formatMessage({ id: 'Reviews' }),
        render: (x: any) => (
          <ShowContext
            data={x}
            language={language}
            showSentiment={showSentiment}
          />
        ),
      },
      {
        key: 'comment_time',
        header: formatMessage({ id: 'Time' }),
        width: 200,
      },
      {
        key: 'tweets_like',
        header: formatMessage({ id: 'Like' }),
        align: 'right',
        width: 110,
        render: (cvm: any) => {
          if (Number(cvm.tweets_like) < 1) {
            return '-';
          }
          return reviewsFormat(cvm.tweets_like);
        },
      },
      {
        key: 'engagement',
        header: (
          <div className="dbo-tablecommon-header">
            <span> {formatMessage({ id: 'Engagement' })} </span>
            {/* <BubbleCommon tipsMap={engagementTipsText}></BubbleCommon> */}
          </div>
        ),
        align: 'right',
        width: getLocale() === 'en-US' ? 180 : 128,
        render: (cvm: any) => {
          if (Number(cvm.engagement) < 1) {
            return '-';
          }
          return reviewsFormat(cvm.engagement);
        },
      },
    ];

    if (showDetailColumn) {
      list.push({
        key: 'actions',
        header: formatMessage({ id: 'Actions' }),
        align: 'right',
        width: 150,
        render: (x: any) => <ShareBtn text={text} data={x}></ShareBtn>,
      });
    }

    return list;
  }, [showDetailColumn, formatMessage, language]);

  const [pageInfoAddons, noData] = useMemo(() => {
    let allAddons = [];
    if (tableList.length > 0) {
      allAddons.push(
        pageable({
          recordCount: total,
          pageSize: pageInfo.page_size,
          pageIndex: pageInfo.page,
          onPagingChange: (query) => {
            setPageInfo({
              page: query.pageIndex || 0,
              page_size: query.pageSize || 0,
            });
          },
        }),
      );
    }

    return [allAddons, tableList.length === 0];
  }, [tableList, pageInfo]);

  return {
    pageInfoAddons,
    noData,
    tableCommon,
    sentimentInfo,
    loading,
    setSort,
    formatMessage,
    tableList,
    sort,
    total,
    filter: params,
  };
}
